import React, { useEffect, useState, useCallback } from "react";
import { authFetch, C, PERM_LABELS } from "./App";

export function GroupsView({ token }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null); // group object or "new"

  const load = useCallback(() => {
    setLoading(true);
    authFetch("/admin/groups", token)
      .then((d) => setGroups(d.groups || []))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  function remove(id, name) {
    if (!window.confirm(`Delete group "${name}"? Members will lose admin access.`)) return;
    authFetch(`/admin/groups/${id}`, token, { method: "DELETE" })
      .then(load)
      .catch((e) => setErr(e.message));
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111", margin: 0 }}>Groups</h1>
        <button onClick={() => setEditing("new")} style={{ background: C.green, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>+ New Group</button>
      </div>
      <p style={{ fontSize: 13, color: C.gray, margin: "0 0 20px" }}>Define permission sets. Each user belongs to exactly one group.</p>

      {err && <div style={{ color: C.danger, fontSize: 13, marginBottom: 14 }}>{err}</div>}

      {loading ? (
        <div style={{ color: C.gray, fontSize: 13 }}>Loading…</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {groups.map((g) => (
            <div key={g.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{g.name}</div>
                  <div style={{ fontSize: 11.5, color: C.gray, marginTop: 1 }}>{g.member_count} member{g.member_count === 1 ? "" : "s"}</div>
                </div>
              </div>
              {g.description && <div style={{ fontSize: 12.5, color: C.gray, marginBottom: 10 }}>{g.description}</div>}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 14 }}>
                {(g.permissions || []).length === 0
                  ? <span style={{ fontSize: 11, color: C.gray, fontStyle: "italic" }}>No permissions assigned</span>
                  : (g.permissions || []).map((p) => (
                      <span key={p} style={{ fontSize: 10.5, background: C.grayLight, color: "#444", padding: "3px 8px", borderRadius: 7 }}>
                        {PERM_LABELS[p]?.icon} {PERM_LABELS[p]?.label || p}
                      </span>
                    ))
                }
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => setEditing(g)} style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, padding: "7px 0", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Edit</button>
                {g.name !== "Super Admin" && (
                  <button onClick={() => remove(g.id, g.name)} style={{ background: "transparent", border: "1px solid #C6282855", color: "#C62828", borderRadius: 7, padding: "7px 11px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <GroupEditModal
          token={token} group={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function GroupEditModal({ token, group, onClose, onSaved }) {
  const [name, setName] = useState(group?.name || "");
  const [description, setDescription] = useState(group?.description || "");
  const [perms, setPerms] = useState(group?.permissions || []);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isSuperAdmin = group?.name === "Super Admin";

  function toggle(key) {
    if (isSuperAdmin) return; // Super Admin perms locked
    setPerms((p) => (p.includes(key) ? p.filter((x) => x !== key) : [...p, key]));
  }

  function submit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true); setErr("");
    const body = JSON.stringify({ name, description, permissions: perms });
    const call = group
      ? authFetch(`/admin/groups/${group.id}`, token, { method: "PATCH", body })
      : authFetch("/admin/groups", token, { method: "POST", body });
    call.then(onSaved).catch((e) => setErr(e.message)).finally(() => setBusy(false));
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ background: "#fff", borderRadius: 14, padding: 24, width: 440, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{group ? "Edit Group" : "New Group"}</div>
        {err && <div style={{ color: C.danger, fontSize: 12.5, marginBottom: 12 }}>{err}</div>}

        <label style={{ display: "block", fontSize: 11.5, fontWeight: 600, color: C.gray, marginBottom: 5, textTransform: "uppercase" }}>Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} required disabled={isSuperAdmin}
          style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, marginBottom: 12 }} />

        <label style={{ display: "block", fontSize: 11.5, fontWeight: 600, color: C.gray, marginBottom: 5, textTransform: "uppercase" }}>Description</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)}
          style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, marginBottom: 16 }} />

        <label style={{ display: "block", fontSize: 11.5, fontWeight: 600, color: C.gray, marginBottom: 8, textTransform: "uppercase" }}>Permissions</label>
        {isSuperAdmin && <div style={{ fontSize: 11.5, color: C.gray, fontStyle: "italic", marginBottom: 8 }}>Super Admin always has full access — permissions are locked.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
          {Object.entries(PERM_LABELS).map(([key, { label, icon }]) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, cursor: isSuperAdmin ? "default" : "pointer", background: (isSuperAdmin || perms.includes(key)) ? "#F3F8F5" : "#fff" }}>
              <input type="checkbox" checked={isSuperAdmin || perms.includes(key)} disabled={isSuperAdmin} onChange={() => toggle(key)} />
              <span>{icon}</span>{label}
            </label>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={onClose} style={{ flex: 1, background: C.grayLight, border: "none", borderRadius: 8, padding: "10px 0", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button type="submit" disabled={busy} style={{ flex: 1, background: C.green, color: "#fff", border: "none", borderRadius: 8, padding: "10px 0", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{busy ? "Saving…" : "Save Group"}</button>
        </div>
      </form>
    </div>
  );
}
